import {
  STANDARD_LARGE_NUMBER_RATIO,
  STANDARD_NUMBER_HEIGHT,
  STANDARD_NUMBER_WIDTH,
} from '@Animation/constants'
import { KenoAnimationScene } from '@Animation/scenes/keno-animation'
import { ANIMATION_IDENTIFIERS } from '@Animation/types'
import { tweenPromise } from '@Animation/utils'
import { NumberGrid } from './number-grid'
import { SoundController } from '@Animation/sound-controller'

export enum KENO_NUMBER_STATUS {
  DEFAULT = 'DEFAULT',
  SELECTED_PLAYER = 'SELECTED_PLAYER',
  SELECTED_SYSTEM = 'SELECTED_SYSTEM',
  SELECTED_HIT = 'SELECTED_HIT',
}

export class KenoNumber extends Phaser.GameObjects.Container {
  image!: Phaser.GameObjects.Image
  status: KENO_NUMBER_STATUS = KENO_NUMBER_STATUS.DEFAULT
  statusTextureMap: { [key in KENO_NUMBER_STATUS]: string }
  numberStr: string
  number: number
  defaultPosition: Phaser.Math.Vector2

  static matchSound: Phaser.Sound.BaseSound | null = null
  static noMatchSound: Phaser.Sound.BaseSound | null = null

  kenoScene: KenoAnimationScene
  animationIdentifier: ANIMATION_IDENTIFIERS

  tempNumber: Phaser.GameObjects.Image | null = null
  tempBackground: Phaser.GameObjects.Image | null = null

  glow: Phaser.GameObjects.Image | null = null

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    number: number,
    isPlayerSelected: boolean,
    animationIdentifier: ANIMATION_IDENTIFIERS,
    soundConfig?: {
      matchSound: string,
      noMatchSound: string
    }
  ) {
    super(scene, x, y)
    scene.add.existing(this)

    this.animationIdentifier = animationIdentifier
    this.kenoScene = scene as KenoAnimationScene

    this.status = isPlayerSelected ? KENO_NUMBER_STATUS.SELECTED_PLAYER : KENO_NUMBER_STATUS.DEFAULT

    this.number = number
    this.numberStr = number < 10 ? `0${number}` : `${number}`

    this.statusTextureMap = this.createStatusTextureMap()

    this.defaultPosition = new Phaser.Math.Vector2(x, y)

    if(soundConfig && KenoNumber.matchSound === null && KenoNumber.noMatchSound === null) {
      KenoNumber.matchSound = scene.sound.add(soundConfig.matchSound)
      KenoNumber.noMatchSound = scene.sound.add(soundConfig.noMatchSound)
    }
  }

  async addImage(): Promise<void> {
    return new Promise((resolve) => {
      this.image = this.scene.add.image(0, 0, this.statusTextureMap[this.status], this.number - 1)

      this.image.setOrigin(0.5, 0.5)
      this.add(this.image)
      resolve()
    })
  }

  getGlowAtlasFrame() {
    return ''
  }

  getGlowAtlas() {
    return ``
  }

  createGlowBackground() {
    if (this.glow) {
      this.glow.destroy()
    }
    this.glow = this.scene.add.image(0, 0, this.getGlowAtlas(), this.getGlowAtlasFrame())
    this.add(this.glow)
    this.sendToBack(this.glow)

    const numberGrid = this.parentContainer as NumberGrid
    numberGrid.addNumberToGlowEffect(this.glow)

    return this.glow
  }

  createStatusTextureMap(): { [key in KENO_NUMBER_STATUS]: string } {
    return {
      [KENO_NUMBER_STATUS.DEFAULT]: 'foo',
      [KENO_NUMBER_STATUS.SELECTED_PLAYER]: 'foo',
      [KENO_NUMBER_STATUS.SELECTED_SYSTEM]: 'foo',
      [KENO_NUMBER_STATUS.SELECTED_HIT]: 'foo',
    }
  }

  updateImageTexture() {
    this.image.setTexture(this.statusTextureMap[this.status], this.number - 1)
    if (this.status === KENO_NUMBER_STATUS.SELECTED_HIT) {
      this.createGlowBackground()
      this.glow?.setVisible(true)
    } else {
      this.glow?.setVisible(false)
    }
  }

  async transitionToSystemSelected() {
    this.status = KENO_NUMBER_STATUS.SELECTED_SYSTEM
    this.updateImageTexture()
  }

  async transitionToSelectedHit() {
    this.status = KENO_NUMBER_STATUS.SELECTED_HIT
    this.updateImageTexture()
  }

  calculatePopPositionInFrame() {
    let newX = this.x
    let newY = this.y

    const maxX = STANDARD_NUMBER_WIDTH * 9
    const maxY = STANDARD_NUMBER_HEIGHT * 7

    if (this.x < STANDARD_NUMBER_WIDTH / 2) {
      newX += STANDARD_NUMBER_WIDTH / 2
    } else if (this.x + STANDARD_NUMBER_WIDTH / 2 > maxX) {
      newX -= STANDARD_NUMBER_WIDTH / 2
    }

    if (this.y < STANDARD_NUMBER_WIDTH / 2) {
      newY += STANDARD_NUMBER_WIDTH / 2
    } else if (this.y + STANDARD_NUMBER_WIDTH / 2 > maxY) {
      newY -= STANDARD_NUMBER_WIDTH / 2
    }

    return {
      x: newX,
      y: newY,
    }
  }

  async popNumberTexture(textureName: string, startScale: number, largeScale: number) {
    this.tempNumber?.destroy()

    this.tempNumber = this.scene.add.image(this.x, this.y, textureName, this.number - 1)
    this.tempNumber.setOrigin(0.5, 0.5)
    this.tempNumber.setScale(STANDARD_LARGE_NUMBER_RATIO)

    this.kenoScene.addToTopLayerNumberGridContainer(this.tempNumber)

    this.image.setVisible(false)

    await this.popEffect(this.tempNumber, startScale, largeScale)

    this.tempNumber.destroy()
    this.image.setVisible(true)
  }

  async popEffect(
    popObject: Phaser.GameObjects.Image | Phaser.GameObjects.Container = this,
    defaultScale: number = STANDARD_LARGE_NUMBER_RATIO,
    largeScale: number = 1,
  ) {
    const originalPosition = new Phaser.Math.Vector2(popObject.x, popObject.y)
    const newPosition = this.calculatePopPositionInFrame()

    await tweenPromise(this.scene, {
      targets: popObject,
      scale: largeScale,
      x: newPosition.x,
      y: newPosition.y,
      duration: 100,
    })()
    await tweenPromise(this.scene, {
      targets: popObject,
      scale: largeScale,
      duration: 500,
    })()
    await tweenPromise(this.scene, {
      targets: popObject,
      scale: defaultScale,
      x: originalPosition.x,
      y: originalPosition.y,
      duration: 133,
    })()
  }

  async select(skipping: boolean) {
    this.parentContainer.moveTo(this, this.parentContainer.length - 1)

    switch (this.status) {
      case KENO_NUMBER_STATUS.DEFAULT:
      case KENO_NUMBER_STATUS.SELECTED_SYSTEM:
        if(KenoNumber.noMatchSound && !skipping) {
          SoundController.playSound(KenoNumber.noMatchSound)
        }
        await this.transitionToSystemSelected()
        break
      case KENO_NUMBER_STATUS.SELECTED_PLAYER:
      case KENO_NUMBER_STATUS.SELECTED_HIT:
        if(KenoNumber.matchSound && !skipping) {
          SoundController.playSound(KenoNumber.matchSound)
        }
        await this.transitionToSelectedHit()
        break
      default:
        break
    }

    this.setDepth(1)
  }

  async skip() {
    this.setPosition(this.defaultPosition.x, this.defaultPosition.y)
    await this.select(true)
    this.setScale(1)
    this.tempNumber?.destroy()
    this.tempBackground?.destroy()
  }

  destroy(fromScene?: boolean): void {
    this.tempNumber?.destroy()
    this.tempBackground?.destroy()
    KenoNumber.noMatchSound = null
    KenoNumber.matchSound = null
    this.removeAll(true)
    super.destroy(fromScene)
  }
}
